// Client-side twin of proxy-server `internal/ontology/triggers/describe.go`.
// Renders a Condition tree as a natural-language sentence so config editors
// can show readable plaintext for leaf rows / trigger previews without waiting
// on /evaluate. Keep wording in lockstep with the Go tests.

import type {
  AggConditionLeaf,
  AlarmActiveConditionLeaf,
  Condition,
  ConditionCmp,
  ConditionFn,
  ConditionLeaf,
  ConditionScalar,
  DeviationConditionLeaf,
  DeviationMetric,
  ExprConditionLeaf,
  LiveConditionLeaf
} from './conditions'

/** Short badge label for a leaf kind (design: Live / Agg / Deviation / Alarm). */
export const LEAF_KIND_BADGE: Record<ConditionLeaf['kind'], string> = {
  live: 'Live',
  agg: 'Agg',
  deviation: 'Deviation',
  alarm_active: 'Alarm',
  expr: 'Expr'
}

/**
 * Prefers an author-supplied `description` (whitespace-only counts as empty)
 * and falls back to {@link describeCondition}. Mirrors Go `ResolveDescription`.
 */
export function resolveConditionDescription(c: Condition): string {
  const authored = c.description?.trim()
  if (authored) return authored
  return describeCondition(c)
}

/**
 * Single-sentence English explanation of when `c` would fire.
 * Capitalizes the first character; does not append a period.
 * Mirrors Go `DescribeCondition`.
 */
export function describeCondition(c: Condition): string {
  const s = describeConditionInner(c)
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function describeConditionInner(c: Condition): string {
  switch (c.kind) {
    case 'live':
      return describeLive(c)
    case 'agg':
      return describeAgg(c)
    case 'deviation':
      return describeDeviation(c)
    case 'alarm_active':
      return describeAlarmActive(c)
    case 'expr':
      return describeExpr(c)
    case 'and':
      return describeComposite(c.operands, ' and ', 'all of the following hold')
    case 'or':
      return describeComposite(c.operands, ' or ', 'any of the following hold')
    case 'not':
      return c.operand ? `it is not the case that ${describeConditionInner(c.operand)}` : ''
    default:
      return ''
  }
}

function describeLive(c: LiveConditionLeaf): string {
  return `${tagOrPlaceholder(c.tag_path)} is ${cmpPhrase(c.cmp, c.value)}`
}

function describeAgg(c: AggConditionLeaf): string {
  const subject = aggSubject(c.fn, tagOrPlaceholder(c.tag_path), windowPhrase(c.window))
  return `${subject} is ${cmpPhrase(c.cmp, c.value)}`
}

function describeDeviation(c: DeviationConditionLeaf): string {
  const tag = tagOrPlaceholder(c.tag_path)
  const baselineFn = c.baseline_fn ?? 'avg'
  const window = windowPhrase(c.window)
  const baseline = aggSubject(baselineFn, tag, window)
  const metric: DeviationMetric | undefined = c.metric

  switch (metric) {
    case 'abs_diff':
      return `${tag} differs from ${baseline} by ${cmpPhrase(c.cmp, c.value)}`
    case 'pct_from_baseline':
      return `${tag} differs from ${baseline} by ${cmpPhrase(c.cmp, c.value)} percent`
    case 'zscore':
      return `${tag} is ${cmpPhrase(c.cmp, c.value)} standard deviations from ${baseline}`
    default:
      return `${tag} deviates from ${baseline} by ${cmpPhrase(c.cmp, c.value)}`
  }
}

function describeAlarmActive(c: AlarmActiveConditionLeaf): string {
  const tag = tagOrPlaceholder(c.tag_path)
  const name = c.alarm_name.trim() || '?'
  // Go fmt %q quotes the alarm name with double quotes.
  return `the "${name}" alarm is active on ${tag}`
}

function describeExpr(c: ExprConditionLeaf): string {
  const expr = c.expression.trim() || '?'
  return `${expr} is ${cmpPhrase(c.cmp, c.value)}`
}

function describeComposite(children: Condition[], joiner: string, fallback: string): string {
  if (children.length === 0) return ''
  if (children.length === 1) return describeConditionInner(children[0])
  const parts: string[] = []
  for (const ch of children) {
    const s = describeConditionInner(ch)
    if (s) parts.push(s)
  }
  if (parts.length === 0) return fallback
  return parts.join(joiner)
}

function aggSubject(fn: ConditionFn | string, tag: string, window: string): string {
  switch (fn) {
    case 'avg':
      return `the average of ${tag} over ${window}`
    case 'min':
      return `the minimum of ${tag} over ${window}`
    case 'max':
      return `the maximum of ${tag} over ${window}`
    case 'last':
      return `the latest value of ${tag} in ${window}`
    case 'stddev':
      return `the standard deviation of ${tag} over ${window}`
    case 'delta':
      return `the change in ${tag} over ${window}`
    default:
      return `the ${fnOrPlaceholder(fn)} of ${tag} over ${window}`
  }
}

function cmpPhrase(cmp: ConditionCmp | string, value: ConditionScalar): string {
  const v = valuePhrase(value)
  switch (cmp) {
    case 'gt':
      return `greater than ${v}`
    case 'gte':
      return `at least ${v}`
    case 'lt':
      return `less than ${v}`
    case 'lte':
      return `at most ${v}`
    case 'eq':
      return `equal to ${v}`
    case 'ne':
      return `not equal to ${v}`
    default:
      return `${cmp} ${v}`
  }
}

function valuePhrase(value: ConditionScalar): string {
  if (typeof value === 'boolean') return String(value)
  if (typeof value === 'string') return JSON.stringify(value)
  if (!Number.isFinite(value)) return '?'
  // Match Go strconv FormatFloat 'g'/6 — trim float jitter, keep readable.
  return Number(value.toPrecision(6)).toString()
}

function tagOrPlaceholder(s: string | undefined): string {
  return (s ?? '').trim() === '' ? '?' : s!
}

function fnOrPlaceholder(s: string): string {
  return s.trim() === '' ? '?' : s
}

/**
 * Renders a duration token like "5m" / "2h30m" / "24h" as
 * "the last 5 minutes" / "the last 2 hours 30 minutes" / "the last 1 day".
 * Mirrors Go `windowPhrase` + `ParseWindow`.
 */
export function windowPhrase(window: string): string {
  const secs = parseWindowSeconds(window)
  if (secs === null) {
    if (!window || window.trim() === '') return 'the configured window'
    return `the last ${window}`
  }
  let remaining = secs
  const parts: string[] = []
  const units: Array<{ size: number; one: string; many: string }> = [
    { size: 86400, one: 'day', many: 'days' },
    { size: 3600, one: 'hour', many: 'hours' },
    { size: 60, one: 'minute', many: 'minutes' },
    { size: 1, one: 'second', many: 'seconds' }
  ]
  for (const u of units) {
    const n = Math.floor(remaining / u.size)
    if (n === 0) continue
    remaining -= n * u.size
    parts.push(n === 1 ? `1 ${u.one}` : `${n} ${u.many}`)
  }
  if (parts.length === 0) return `the last ${window}`
  return `the last ${parts.join(' ')}`
}

/** Parse Go-style duration strings used as condition windows. Returns seconds or null. */
export function parseWindowSeconds(window: string): number | null {
  const s = window.trim()
  if (!s) return null
  // Go time.ParseDuration: optional leading sign, then sequences of number+unit
  // (ns|us|µs|ms|s|m|h). We only need s/m/h for RCA windows.
  const re = /^(\d+(?:\.\d+)?)(ns|us|µs|μs|ms|s|m|h)(?:(\d+(?:\.\d+)?)(ns|us|µs|μs|ms|s|m|h))*$/
  if (!re.test(s)) return null
  let totalMs = 0
  const partRe = /(\d+(?:\.\d+)?)(ns|us|µs|μs|ms|s|m|h)/g
  let m: RegExpExecArray | null
  while ((m = partRe.exec(s))) {
    const n = Number(m[1])
    if (!Number.isFinite(n)) return null
    switch (m[2]) {
      case 'ns':
        totalMs += n / 1e6
        break
      case 'us':
      case 'µs':
      case 'μs':
        totalMs += n / 1e3
        break
      case 'ms':
        totalMs += n
        break
      case 's':
        totalMs += n * 1000
        break
      case 'm':
        totalMs += n * 60_000
        break
      case 'h':
        totalMs += n * 3_600_000
        break
      default:
        return null
    }
  }
  const secs = totalMs / 1000
  if (secs <= 0) return null
  return Math.round(secs)
}

export function isConditionLeaf(c: Condition): c is ConditionLeaf {
  return (
    c.kind === 'live' ||
    c.kind === 'agg' ||
    c.kind === 'deviation' ||
    c.kind === 'alarm_active' ||
    c.kind === 'expr'
  )
}
